import 'leaflet-velocity'
import React from 'react'
import PropTypes from 'prop-types'
import 'leaflet-timedimension'
import {
    Container,
    Box,
    Typography,
    Paper,
    Stack,
    Chip,
    Divider,
} from '@mui/material'
import DateUtils from '../../inumet_js_lib/utils/dateUtils'
import WaterIcon from '@mui/icons-material/Water'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

// ─── encabezado de sección ────────────────────────────────────────────────────

const SeccionHeader = ({ icon, titulo, fecha }) => (
    <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
    >
        <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>
        <Typography
            variant="h6"
            component="h2"
            fontWeight={700}
            sx={{ flexGrow: 1 }}
        >
            {titulo}
        </Typography>
        <Chip label={fecha} size="small" variant="outlined" />
    </Stack>
)

SeccionHeader.propTypes = {
    icon: PropTypes.node,
    titulo: PropTypes.string,
    fecha: PropTypes.string,
}

// ─── componente ───────────────────────────────────────────────────────────────

const AvisosMarino = ({ dataAvisos }) => {
    if (!dataAvisos) return <></>

    const hayAvisos = Boolean(
        dataAvisos.activo &&
            dataAvisos.zonasDescripcion &&
            dataAvisos.zonasDescripcion.length > 0
    )

    const fechaLTIniValidez =
        dataAvisos.fechaInicioValidez + ' ' + dataAvisos.horaInicioValidez
    const utcIniValidez = DateUtils.addHours(fechaLTIniValidez, 3)
    const fechaStr =
        DateUtils.latinDT(fechaLTIniValidez) +
        ' (' +
        DateUtils.latinDT(utcIniValidez) +
        ' UTC)'

    return (
        <Container maxWidth="md" disableGutters sx={{ py: 1 }}>

            {/* Encabezado institucional */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" component="h1" fontWeight={700}>
                    Instituto Uruguayo de Meteorología
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                >
                    Área de Meteorología y Clima para la Sociedad
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    División Servicios Meteorológicos · Pronóstico del Tiempo y
                    Vigilancia Meteorológica
                </Typography>
            </Box>

            {/* Situación sinóptica */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <SeccionHeader
                    icon={<WaterIcon />}
                    titulo="Situación Sinóptica"
                    fecha={fechaStr}
                />
                <Typography
                    variant="body1"
                    sx={{ lineHeight: 1.7, textAlign: 'justify' }}
                >
                    {dataAvisos.situacionSinoptica}
                </Typography>
            </Paper>

            {/* Avisos */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <SeccionHeader
                    icon={<WarningAmberIcon />}
                    titulo="Avisos"
                    fecha={fechaStr}
                />

                {hayAvisos ? (
                    <Stack spacing={2}>
                        {dataAvisos.zonasDescripcion.map(
                            (itemDataAviso, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        bgcolor: 'warning.50',
                                        borderLeft: '4px solid',
                                        borderColor: 'warning.main',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ mb: 0.5 }}
                                    >
                                        <WarningAmberIcon
                                            fontSize="small"
                                            sx={{ color: 'warning.dark' }}
                                        />
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                        >
                                            {itemDataAviso.zonaNombre} por{' '}
                                            {itemDataAviso.tipo}
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        variant="body2"
                                        sx={{ lineHeight: 1.7 }}
                                    >
                                        {itemDataAviso.descripcion}
                                    </Typography>
                                </Box>
                            )
                        )}
                    </Stack>
                ) : (
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                            p: 2,
                            borderRadius: 1,
                            bgcolor: 'success.50',
                            borderLeft: '4px solid',
                            borderColor: 'success.main',
                        }}
                    >
                        <CheckCircleOutlineIcon
                            sx={{ color: 'success.main' }}
                        />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                                Sin avisos vigentes
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                No se formula aviso para el período.
                            </Typography>
                        </Box>
                    </Stack>
                )}

                <Divider sx={{ my: 2.5 }} />

                <Typography variant="caption" color="text.secondary">
                    La Situación Sinóptica en superficie y los Avisos se
                    actualizan 4 veces al día, a las 00, 06, 12 y 18 hora local
                    (03, 09, 15 y 21 UTC).
                </Typography>
            </Paper>

        </Container>
    )
}

export default AvisosMarino

AvisosMarino.propTypes = {
    dataAvisos: PropTypes.object,
}
